import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router";
import { isOperatorSessionLost } from "@/shared/api/errors";
import { can, type AdminAction } from "@/shared/auth/roles";
import { OperatorProvider, useOperatorMeQuery, useOperatorSession } from "@/shared/auth/session";
import { FailureCard } from "@/shared/components/FailureCard";
import { PageSkeleton } from "@/shared/components/PageSkeleton";
import { ROUTES } from "@/shared/constants/routes";

/** Loads /internal/me once and hands the operator to the shell; a lost operator session goes to the login page. */
export function RequireOperator({ children }: { children: ReactNode }) {
  const me = useOperatorMeQuery();
  const location = useLocation();

  if (me.isPending) {
    return (
      <div className="mx-auto max-w-3xl p-6">
        <PageSkeleton />
      </div>
    );
  }
  if (me.isError) {
    if (isOperatorSessionLost(me.error)) {
      return <Navigate to={ROUTES.login} replace state={{ from: location.pathname + location.search }} />;
    }
    return (
      <div className="p-6">
        <FailureCard
          title="Could not load your operator account"
          description="The server did not answer. Check your connection and try again."
          onRetry={() => void me.refetch()}
          hideHome
          details={me.error.message}
        />
      </div>
    );
  }
  return <OperatorProvider value={me.data}>{children}</OperatorProvider>;
}

/** Admin-only pages render the failure card for an operator who typed the address; the nav never offers them. */
export function RequireAdmin({ action, children }: { action: AdminAction; children: ReactNode }) {
  const { operator } = useOperatorSession();
  if (can(operator.role, action)) return children;
  return (
    <div className="py-12">
      <FailureCard
        title="Only platform admins can open this"
        description="Your operator role reads everything, opens support sessions and retries jobs; an admin manages operators, reviewer codes and organisation lifecycle."
      />
    </div>
  );
}
